<template>
	<app-page>
		<app-heading title="Integrations Status">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.integration"
							label="Integrations"
							:items="integrations"
							@change="fetchConnections"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.integration_connection"
							label="Connections"
							:items="connections"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.status"
							label="Status"
							:items="statuses"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-text
						v-model="filters.unit_code"
						label="Unit Code"
						clearable
						dense
					></input-text>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="entries"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:sort-by="'unit_code'"
					@update="fetchStatuses"
				>
					<template v-slot:integration="{ item }">
						<div>{{ getIntegrationName(item.integration) }}</div>
					</template>
					<template v-slot:healthy="{ item }">
						<span
							v-if="item.healthy"
							class="success--text"
						>Healthy ({{ getHealthyNumbers(item) }})</span>
						<span
							v-else
							class="error--text"
						>Unhealthy ({{ getHealthyNumbers(item) }})</span>
					</template>
					<template v-slot:identity="{ item }">
						<span v-if="item.identity && item.identity.listing_id">
							<b>Listing: </b>
							<a
								v-if="getIntegrationSlug(item.integration) === 'airbnb'"
								:href="`https://www.airbnb.com/rooms/${item.identity.listing_id}`"
								target="_blank"
							>
								{{ item.identity.listing_id }}
							</a>
							<a
								v-else-if="getIntegrationSlug(item.integration) === 'homeaway'"
								:href="`https://www.vrbo.com/${item.identity.listing_id}?dateless=true`"
								target="_blank"
							>
								{{ item.identity.listing_id }}
							</a>
							<span v-else>{{ item.identity.listing_id }}</span>
						</span>
						<span v-if="item.identity && item.identity.supplier_id">
							<b>Supplier: </b>{{ item.identity.supplier_id }}
						</span>
						<span v-if="item.identity && item.identity.property_id">
							<b>Property: </b>
							<a
								v-if="getIntegrationSlug(item.integration) === 'expedia'"
								:href="`https://www.expedia.com/hotels.h${item.identity.property_id}.Hotel-Information`"
								target="_blank"
							>
								{{ item.identity.property_id }}
							</a>
							<span v-else>{{ item.identity.property_id }}</span>
						</span>
						<span v-if="item.identity && item.identity.room_type_id" class="ml-2">
							<b>Room Type: </b>{{ item.identity.room_type_id }}
						</span>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							table
							@click="onStatusDetails(item)"
						>
							Details
						</app-button>
						<app-button
							color="secondary"
							table
							target="_blank"
							:to="{
								name: 'integrations-logs',
								query: {
									integration: item.integration,
									integration_connection: item.integration_connection,
									unit_code: item.unit_code,
								},
							}"
						>
							Logs
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>

		<app-dialog
			v-model="details.show"
			max-width="800"
			height="600"
			max-height="600"
			:title="details.title"
		>
			<app-dialog-body>
				<app-data-table
					:items="details.entries"
					:dynamic="false"
					:headers="details.headers"
					:sort-by="'category'"
					@update="fetchStatuses"
				>
					<template v-slot:integration="{ item }">
						<div>{{ getIntegrationName(item.integration) }}</div>
					</template>
					<template v-slot:identity="{ item }">
						{{ item.identity.listing_id }}
					</template>
					<template v-slot:healthy="{ item }">
						<span
							v-if="item.healthy"
							class="success--text"
						>Healthy</span>
						<span
							v-else
							class="error--text"
						>Unhealthy</span>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							table
							@click="onStatusDetails(item)"
						>
							Details
						</app-button>
					</template>
				</app-data-table>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat="flat"
					@click="details.show = false"
				>Close</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import filters from '@/mixins/filters'
import { mapSelectOptions, dynamicNewMapEntries } from '@/utils/maps'

export default {
	mixins: [filters],
	async created() {
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get())

		if (this.filters.integration) {
			this.fetchConnections()
		}
	},
	data() {
		return {
			entries: [],
			integrationsMap: {},
			connections: [],
			integrations: [],
			count: -1,
			details: {
				show: false,
				entries: [],
				title: '',
				headers: [{
					text: 'Category',
					value: 'category',
					sortable: true,
				}, {
					text: 'Status',
					value: 'healthy',
					sortable: true,
				}, {
					text: 'Error',
					value: 'error',
					sortable: false,
				}],
			},
			refresh: false,
			statuses: [{
				text: 'Healthy',
				value: true,
			}, {
				text: 'Unhealthy',
				value: false,
			}],
			headers: [{
				text: 'Unit',
				value: 'unit_code',
				sortable: true,
			}, {
				text: 'Integration',
				value: 'integration',
				sortable: true,
			}, {
				text: 'Status',
				value: 'healthy',
				sortable: true,
			}, {
				text: 'Last Synchronization',
				value: 'last_sync',
				sortable: true,
				format: 'datetime',
			}, {
				text: 'Identity',
				value: 'identity',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				sortable: false,
				align: 'right',
			}],
			filters: {
				integration: '',
				integration_connection: '',
				unit_code: '',
				status: '',
			},
		}
	},
	methods: {
		getIntegrationSlug(id) {
			return this.integrationsMap[id] && this.integrationsMap[id].slug
				? this.integrationsMap[id].slug
				: undefined
		},
		getIntegrationName(id) {
			return this.integrationsMap[id] && this.integrationsMap[id].name
				? this.integrationsMap[id].name
				: 'UNDEFINED'
		},

		getHealthyNumbers(item) {
			const total = Object.keys(item.details || {}).length
			const healthyNb = Object.keys(item.details || {}).filter(key => item.details[key].healthy).length

			return `${healthyNb}/${total}`
		},

		async updateIntegrationsMap() {
			const res = await dynamicNewMapEntries(
				this.entries,
				'integration',
				this.integrationsMap,
				this.$ravello.request()
					.v(2)
					.to('integrations')
					.fields(['name', 'slug']),
			)

			if (res.err) {
				this.$notifications.pushAlert('error', res.err.data ? res.err.data.message : res.err.message)
			}

			this.integrationsMap = {
				...this.integrationsMap,
				...res.entries,
			}
		},

		async fetchConnections() {
			if (this.filters.integration) {
				this.connections = mapSelectOptions(await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.filters.integration)
					.to('connections')
					.field(['name'])
					.limit(100)
					.get())
			} else {
				this.connections = []
				this.filters.integration_connection = null
			}
		},

		async fetchStatuses(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('integrationstatuses')
				.fromPagination(pagination)

			if (this.appliedFilters.integration) {
				request.param('integration', this.appliedFilters.integration)
			}
			if (this.appliedFilters.integration_connection) {
				request.param('integration_connection', this.appliedFilters.integration_connection)
			}
			if (this.appliedFilters.unit_code) {
				request.param('unit_code', this.appliedFilters.unit_code)
			}
			if (typeof this.appliedFilters.status !== 'undefined') {
				request.param('healthy', this.appliedFilters.status)
			}

			try {
				this.entries = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}

				await this.updateIntegrationsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		onStatusDetails(item) {
			this.title = `${item.unit_code} - ${this.getIntegrationName(item.integration)}`
			this.details.entries = Object.keys(item.details || {}).reduce((acc, key) => [
				...acc,
				{
					category: key.split('_')
						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
						.join(' '),
					healthy: item.details[key].healthy,
					error: item.details[key].error,
				},
			], [])

			this.details.show = true
		},
	},
}
</script>
